import { Cancel, CheckRounded, Edit, NewReleases } from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Reservation } from '../../apiTypes';
import BookingsBadge from '../../components/BookingsBadge';
import DataTable, { Column } from '../../components/DataTable';
import PrimaryButton from '../../components/PrimaryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useCollection } from '../../utils/api';
import getDayDateRange from '../../utils/dates/getDayDateRange';
import EditReservaion from './EditReservaion';

type Row = DbDocument<Reservation>;

const Booking = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const { lang } = useLocale();
  const { cafeSlug, tablesByIds } = useAdmin();
  const { unConfirmedReservations } = useCafe();

  const [isOpen, setIsOpen] = useState(false);
  const [reservation, setReservation] = useState<DbDocument<Reservation> | null>(null);
  const [tab, setTab] = useState<'all' | 'unConfirmed'>('all');

  const dateRange = useMemo(() => getDayDateRange(date), [date]);

  const { data: reservations } = useCollection<Reservation>(`cafes/${cafeSlug}/reservations`, {
    where: ['date', '>', dateRange.start],
    where2: ['date', '<', dateRange.end],
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'status',
        title: '',
        align: 'left',
        render: (r: Row) => {
          if (r.canceled) {
            return <Cancel color="error" />;
          }
          if (r.confirmed) {
            return <CheckRounded color="success" />;
          }
          return <NewReleases color="warning" />;
        },
      },
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => <Typography>{format(r.date, 'dd.MM')}</Typography>,
      },
      {
        name: 'time',
        title: t.time[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.time}</Typography>,
      },
      {
        name: 'name',
        title: t.userName[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
      },
      {
        name: 'phone',
        title: t.phone[lang],
        align: 'left',
        render: (r: Row) => (
          <Box
            sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', color: (t) => t.palette.text.primary }}
            component="a"
            href={`tel:${r.phone}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography>{r.phone}</Typography>
          </Box>
        ),
      },
      {
        name: 'tableName',
        title: t.table[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.tableId ? tablesByIds[r.tableId]?.name : '-'}</Typography>,
      },
      {
        name: 'guests',
        title: t.quantity_of_visitors[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.guests}</Typography>,
      },
      {
        name: 'edit',
        title: '',
        align: 'right',
        render: (r: Row) => (
          <IconButton onClick={() => setReservation(r)} disabled={r.canceled}>
            <Edit />
          </IconButton>
        ),
      },
    ],
    [lang, tablesByIds]
  );

  const handleClose = () => {
    setIsOpen(false);
    setReservation(null);
  };

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <BookingsBadge>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3 }}
        >
          <Tab label={t.all_bookings[lang]} value="all" />
          <Tab label={t.unconfirmed_bookings[lang]} value="unConfirmed" />
        </Tabs>
      </BookingsBadge>

      {tab === 'all' && (
        <DataTable
          columns={columns}
          rows={reservations || []}
          perPage={10}
          onRowClick={(r) => setReservation(r)}
          dataTestIdPrefix="reservation"
          actionButton={
            <PrimaryButton onClick={() => setIsOpen(true)} variant="contained">
              {t.add_booking[lang]}
            </PrimaryButton>
          }
          customHeader={
            <DatePicker
              label={t.date[lang]}
              value={date}
              onChange={(newValue) => setDate(newValue)}
              renderInput={(params) => <StyledTextField {...params} size="small" />}
            />
          }
        />
      )}

      {tab === 'unConfirmed' && (
        <DataTable
          columns={columns}
          rows={unConfirmedReservations}
          perPage={10}
          onRowClick={(r) => setReservation(r)}
          dataTestIdPrefix="reservation"
          actionButton={
            <PrimaryButton onClick={() => setIsOpen(true)} variant="contained">
              {t.add_booking[lang]}
            </PrimaryButton>
          }
        />
      )}

      {(reservation || isOpen) && <EditReservaion reservaion={reservation} onClose={handleClose} />}
    </Box>
  );
};

export default Booking;
